import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes
} from 'firebase/storage';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  GraduationCap,
  Pencil,
  Search,
  Sparkles,
  UserRound,
  UsersRound,
  X
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';
import { db, storage } from '../../core/firebase/firebase';
import { useAuth } from '../../core/auth/AuthProvider';
import { PERMISSIONS } from '../../core/permissions/roles';
import { classNameFromId, studentClassId, useSchoolClasses } from '../../core/school/classes';
import { initializeSchoolCatalog } from '../../core/school/catalog';


const TONES = ['violet', 'blue', 'cyan', 'green', 'amber', 'orange', 'rose'];

function text(value) {
  return String(value ?? '').trim();
}

function normalize(value) {
  return text(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function studentName(student) {
  return text(student.fullName || student.fullname || [student.firstName, student.lastName].filter(Boolean).join(' ')) || 'Élève sans nom';
}

function isAcceptedStudent(student) {
  const status = normalize(student.status || 'active');
  return !['archived', 'archive', 'rejected', 'rejetee', 'cancelled', 'annule'].includes(status);
}

function formatBirthDate(value) {
  if (!value) return '—';
  if (typeof value?.toDate === 'function') return value.toDate().toLocaleDateString('fr-FR');
  const raw = text(value);
  const parsed = new Date(raw);
  return raw && !Number.isNaN(parsed.getTime()) ? parsed.toLocaleDateString('fr-FR') : (raw || '—');
}

function initials(name) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'EL';
}

export default function ClassesPage() {
  const { user, can } = useAuth();
  const canManage = can(PERMISSIONS.CLASSES_MANAGE);
  const { classes, usingFallback, error: classesError } = useSchoolClasses();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [search, setSearch] = useState('');
  const [editingClassId, setEditingClassId] = useState('');
  const [classNameDraft, setClassNameDraft] = useState('');
  const [savingClass, setSavingClass] = useState(false);
  const [catalogMessage, setCatalogMessage] = useState('');
  const [timetableUrl, setTimetableUrl] = useState('');
const [timetableLoading, setTimetableLoading] = useState(false);
const [timetableUploading, setTimetableUploading] = useState(false);
const [timetableMessage, setTimetableMessage] = useState('');

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'students'),
      (snapshot) => {
        const rows = snapshot.docs.map((row) => ({ id: row.id, ...row.data() }));
        rows.sort((a, b) => studentName(a).localeCompare(studentName(b), 'fr'));
        setStudents(rows);
        setLoading(false);
        setError('');
      },
      (loadError) => {
        console.error(loadError);
        setError('Impossible de charger les classes. Vérifiez les autorisations Firestore.');
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  const acceptedStudents = useMemo(
    () => students.filter((student) => isAcceptedStudent(student) && studentClassId(student, classes)),
    [students, classes]
  );

  const classStats = useMemo(() => classes.map((schoolClass, index) => ({
    ...schoolClass,
    label: schoolClass.name || schoolClass.className || schoolClass.id,
    tone: TONES[index % TONES.length],
    students: acceptedStudents.filter((student) => studentClassId(student, classes) === schoolClass.id)
  })), [classes, acceptedStudents]);

  const globalStats = useMemo(() => {
    const nonEmpty = classStats.filter((item) => item.students.length > 0);
    const fullest = nonEmpty.length ? [...nonEmpty].sort((a, b) => b.students.length - a.students.length)[0] : null;
    return {
      total: acceptedStudents.length,
      classes: classStats.length,
      fullest,
      average: classStats.length ? Math.round(acceptedStudents.length / classStats.length) : 0
    };
  }, [acceptedStudents, classStats]);

  const selectedClass = classStats.find((item) => item.id === selectedClassId) || null;

  const visibleStudents = useMemo(() => {
    if (!selectedClass) return [];
    const needle = normalize(search);
    if (!needle) return selectedClass.students;
    return selectedClass.students.filter((student) => [
      studentName(student), student.parentName, student.parentPhone, student.parentEmail, student.id
    ].map(normalize).join(' ').includes(needle));
  }, [selectedClass, search]);

  async function initializeCatalog() {
    if (!canManage) return;
    setSavingClass(true);
    setCatalogMessage('');
    try {
      await initializeSchoolCatalog(user?.uid || null);
      setCatalogMessage('Référentiel classes et matières initialisé.');
    } catch (catalogError) {
      console.error(catalogError);
      setCatalogMessage("Impossible d'initialiser le référentiel.");
    } finally {
      setSavingClass(false);
    }
  }

  function startRename(schoolClass) {
    setEditingClassId(schoolClass.id);
    setClassNameDraft(schoolClass.name || schoolClass.className || schoolClass.id);
  }

  async function saveClassName() {
    if (!canManage || !editingClassId || !classNameDraft.trim()) return;
    setSavingClass(true);
    try {
      await updateDoc(doc(db, 'classes', editingClassId), {
        name: classNameDraft.trim(),
        updatedAt: serverTimestamp(),
        updatedByUid: user?.uid || null
      });
      setEditingClassId('');
      setClassNameDraft('');
    } catch (renameError) {
      console.error(renameError);
      setError("Le nom de la classe n'a pas pu être modifié.");
    } finally {
      setSavingClass(false);
    }
  }

  function openClass(classId) {
    setSelectedClassId(classId);
    setSearch('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(() => {
  let cancelled = false;

  async function loadClassTimetable() {
    if (!selectedClassId) {
      setTimetableUrl('');
      return;
    }

    setTimetableLoading(true);
    setTimetableMessage('');

    try {
      const fileRef = ref(
        storage,
        `classTimetables/${selectedClassId}/emploi.png`
      );

      const url = await getDownloadURL(fileRef);

      if (!cancelled) {
        setTimetableUrl(url);
      }
    } catch (err) {
      if (err?.code === 'storage/object-not-found') {
        if (!cancelled) {
          setTimetableUrl('');
        }
      } else {
        console.error("Chargement emploi du temps :", err);

        if (!cancelled) {
          setTimetableMessage(
            "Impossible de charger l'emploi du temps."
          );
        }
      }
    } finally {
      if (!cancelled) {
        setTimetableLoading(false);
      }
    }
  }

  loadClassTimetable();

  return () => {
    cancelled = true;
  };
}, [selectedClassId]);

async function uploadClassTimetable(event) {
  const file = event.target.files?.[0];

  event.target.value = '';

  if (!file || !selectedClass) return;

  if (file.type !== 'image/png') {
    setTimetableMessage('Veuillez sélectionner uniquement un fichier PNG.');
    return;
  }

  if (file.size > 10 * 1024 * 1024) {
    setTimetableMessage('Le fichier PNG ne doit pas dépasser 10 Mo.');
    return;
  }

  setTimetableUploading(true);
  setTimetableMessage('');

  try {
    const fileRef = ref(
      storage,
      `classTimetables/${selectedClass.id}/emploi.png`
    );

    await uploadBytes(fileRef, file, {
      contentType: 'image/png',
      cacheControl: 'no-cache,max-age=0,must-revalidate'
    });

    const url = await getDownloadURL(fileRef);

    setTimetableUrl(url);
    setTimetableMessage('Emploi du temps enregistré.');
  } catch (err) {
    console.error("Import emploi du temps :", err);

    setTimetableMessage(
      "Impossible d'importer l'emploi du temps."
    );
  } finally {
    setTimetableUploading(false);
  }
}

async function removeClassTimetable() {
  if (!selectedClass) return;

  const confirmed = window.confirm(
    `Supprimer l'emploi du temps de ${selectedClass.label} ?`
  );

  if (!confirmed) return;

  setTimetableMessage('');

  try {
    const fileRef = ref(
      storage,
      `classTimetables/${selectedClass.id}/emploi.png`
    );

    await deleteObject(fileRef);

    setTimetableUrl('');
    setTimetableMessage('Emploi du temps supprimé.');
  } catch (err) {
    console.error("Suppression emploi du temps :", err);

    setTimetableMessage(
      "Impossible de supprimer l'emploi du temps."
    );
  }
}

  if (selectedClass) {
    return <>
      <PageHeader
        eyebrow="CLASSES"
        title={selectedClass.label}
        description={`${selectedClass.students.length} élève${selectedClass.students.length > 1 ? 's' : ''} accepté${selectedClass.students.length > 1 ? 's' : ''}`}
        action={<button type="button" className="button button-secondary" onClick={() => setSelectedClassId(null)}><ArrowLeft size={17}/>Toutes les classes</button>}
      />

      {(error || classesError) && <div className="dashboard-alert"><div><strong>Une erreur est survenue.</strong><span>{error || classesError}</span></div></div>}

      <section className="class-detail-hero panel">
        <div className={`class-detail-icon class-tone-${selectedClass.tone}`}><GraduationCap size={29}/></div>
        <div className="class-detail-copy"><span>Effectif accepté</span><strong>{loading ? '…' : selectedClass.students.length}</strong><small>Élèves rattachés par l'identifiant stable {selectedClass.id}</small></div>
        <div className="class-detail-progress" aria-hidden="true"><span style={{ width: `${Math.min(100, selectedClass.students.length * 3.4)}%` }}/></div>
      </section>

      <section className="panel class-timetable-manager">
  <div className="class-timetable-manager-head">
    <div>
      <p className="eyebrow">EMPLOI DU TEMPS</p>
      <h2>{selectedClass.label}</h2>
      <p>
        Importez l'emploi du temps PNG qui sera affiché aux parents
        des élèves de cette classe.
      </p>
    </div>

    {canManage && (
      <div className="class-timetable-actions">
        <input
          id={`timetable-upload-${selectedClass.id}`}
          type="file"
          accept="image/png"
          onChange={uploadClassTimetable}
          hidden
        />

        <label
          htmlFor={`timetable-upload-${selectedClass.id}`}
          className="button button-primary"
        >
          {timetableUploading
            ? 'Import en cours…'
            : timetableUrl
              ? 'Remplacer le PNG'
              : 'Importer un PNG'}
        </label>

        {timetableUrl && (
          <button
            type="button"
            className="button button-secondary"
            onClick={removeClassTimetable}
          >
            Supprimer
          </button>
        )}
      </div>
    )}
  </div>

  {timetableMessage && (
    <p className="class-timetable-message">
      {timetableMessage}
    </p>
  )}

  {timetableLoading && (
    <div className="class-empty-state">
      Chargement de l'emploi du temps…
    </div>
  )}

  {!timetableLoading && timetableUrl && (
    <div className="class-timetable-preview">
      <img
        src={timetableUrl}
        alt={`Emploi du temps ${selectedClass.label}`}
      />
    </div>
  )}

  {!timetableLoading && !timetableUrl && (
    <div className="class-empty-state">
      Aucun emploi du temps PNG n'a encore été ajouté.
    </div>
  )}
</section>

      <section className="class-student-toolbar panel">
        <label className="class-search-field"><Search size={18}/><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher un élève ou un parent…"/></label>
        <span className="class-result-count">{visibleStudents.length} résultat{visibleStudents.length > 1 ? 's' : ''}</span>
      </section>

      <section className="panel class-student-panel">
        <div className="class-student-heading"><div><p className="eyebrow">ÉLÈVES ACCEPTÉS</p><h2>Liste de la classe</h2></div><span className="accepted-only-chip"><Sparkles size={14}/> Acceptés uniquement</span></div>
        {loading && <div className="class-empty-state"><UsersRound size={24}/>Chargement des élèves…</div>}
        {!loading && !visibleStudents.length && <div className="class-empty-state"><UserRound size={24}/>{search ? 'Aucun élève ne correspond à la recherche.' : 'Aucun élève accepté dans cette classe.'}</div>}
        {!loading && visibleStudents.length > 0 && <div className="class-student-table-wrap"><table className="class-student-table"><thead><tr><th>Élève</th><th>Date de naissance</th><th>Parent / responsable</th><th>Téléphone</th></tr></thead><tbody>{visibleStudents.map((student) => { const name = studentName(student); return <tr key={student.id}><td><div className="class-student-identity"><span>{initials(name)}</span><div><strong>{name}</strong><small>{selectedClass.label}</small></div></div></td><td>{formatBirthDate(student.birthDate)}</td><td>{text(student.parentName) || '—'}</td><td>{text(student.parentPhone) || '—'}</td></tr>; })}</tbody></table></div>}
      </section>
    </>;
  }

  return <>
    <PageHeader eyebrow="MODULE" title="Classes" description="Référentiel unique des classes et effectifs des élèves acceptés."/>

    {(error || classesError) && <div className="dashboard-alert"><div><strong>Une erreur est survenue.</strong><span>{error || classesError}</span></div></div>}

    {canManage && <section className="panel class-catalog-manager">
      <div>
        <p className="eyebrow">RÉFÉRENTIEL CENTRAL</p>
        <h2>Classes utilisées partout dans la plateforme</h2>
        <p>Le nom peut changer sans casser les inscriptions, élèves ou affectations : les données utilisent l'ID stable de la classe.</p>
        {catalogMessage && <small>{catalogMessage}</small>}
      </div>
      {usingFallback && <button type="button" className="button button-primary" onClick={initializeCatalog} disabled={savingClass}>{savingClass ? 'Initialisation…' : 'Initialiser classes + matières'}</button>}
    </section>}

    <section className="stats-grid classes-stats-grid">
      <StatCard label="Élèves acceptés" value={loading ? '…' : globalStats.total} hint="Toutes les classes" tone="blue"/>
      <StatCard label="Classes" value={globalStats.classes} hint="Référentiel actif" tone="green"/>
      <StatCard label="Classe la plus chargée" value={loading ? '…' : (globalStats.fullest?.students.length ?? 0)} hint={globalStats.fullest?.label || 'Aucun effectif'} tone="amber"/>
      <StatCard label="Moyenne / classe" value={loading ? '…' : globalStats.average} hint="Élèves acceptés" tone="rose"/>
    </section>

    <section className="classes-overview-panel panel">
      <div className="classes-overview-heading"><div><p className="eyebrow">EFFECTIFS PAR NIVEAU</p><h2>Liste des classes</h2><p>La même liste alimente maintenant les inscriptions et les affectations pédagogiques.</p></div><span className="accepted-only-chip"><Sparkles size={14}/> Acceptés uniquement</span></div>
      <div className="classes-card-grid">
        {classStats.map((level) => {
          const count = level.students.length;
          const percentage = globalStats.total ? Math.round((count / globalStats.total) * 100) : 0;
          const editing = editingClassId === level.id;
          return <article key={level.id} className="class-overview-card class-overview-card-managed">
            <button type="button" className="class-card-open" onClick={() => !editing && openClass(level.id)}>
              <div className="class-card-topline"><span className={`class-card-icon class-tone-${level.tone}`}><GraduationCap size={23}/></span><span className="class-card-share">{percentage}%</span></div>
              <div className="class-card-main">
                {editing ? <span className="class-inline-edit" onClick={(event) => event.stopPropagation()}><input value={classNameDraft} onChange={(event) => setClassNameDraft(event.target.value)} autoFocus/><button type="button" onClick={saveClassName} disabled={savingClass}><Check size={16}/></button><button type="button" onClick={() => setEditingClassId('')}><X size={16}/></button></span> : <span>{level.label}</span>}
                <strong>{loading ? '…' : count}</strong><small>élève{count > 1 ? 's' : ''} accepté{count > 1 ? 's' : ''}</small>
              </div>
              <div className="class-card-progress" aria-hidden="true"><span className={`class-progress-${level.tone}`} style={{ width: `${percentage}%` }}/></div>
              <div className="class-card-link">Voir la classe <ChevronRight size={17}/></div>
            </button>
            {canManage && !editing && <button type="button" className="class-rename-button" onClick={() => startRename(level)} title="Renommer la classe"><Pencil size={15}/></button>}
          </article>;
        })}
      </div>
    </section>
  </>;
}
